import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'hw_preorder_info';

export const usePreorderPersistence = () => {
  const [restoredData, setRestoredData] = useState(null);
  const [isPersisted, setIsPersisted] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Save order data before navigation
  const persistOrderData = useCallback((orderData) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(orderData));
      setIsPersisted(true);
      return true;
    } catch (error) {
      console.error('Failed to persist order data:', error);
      return false;
    }
  }, []);

  // Clear persisted data and remove persistData query parameter
  const clearPersistedData = useCallback(() => {
    // Clear the local storage
    localStorage.removeItem(STORAGE_KEY);
    setIsPersisted(false);
    setRestoredData(null);

    // Remove the persistData query parameter if it exists
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('persistData')) {
      searchParams.delete('persistData');

      const newSearch = searchParams.toString();
      const newPath = location.pathname + (newSearch ? `?${newSearch}` : '');

      // Update URL without refreshing the page
      navigate(newPath, { replace: true });
    }
  }, [location.search, navigate]);

  // Check for persisted data on mount
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        setRestoredData(parsed);
        setIsPersisted(true);
      }
    } catch (error) {
      console.error('Error reading persisted order data:', error);
    }
  }, []);

  return {
    restoredData, // Restored data is available immediately for components to use
    hasRestoredData: Boolean(restoredData), // Flag to indicate if data was found and restored
    persistOrderData, // Function to save data before navigation
    clearPersistedData, // Function to clear data (call after applying restored data)
    isPersisted, // Flag to indicate if data is currently persisted
  };
};
